import { useEffect } from "react";
import {
  Box,
  Card,
  Divider,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import CustomTypography from "../../../Components/Typography";
import InputField from "../../../Components/InputField";
import CustomCheckboxLabel from "../../../Components/CustomCheckboxLabel";
import AddressMapInput from "../../../Components/AddressMapInput";
import TimeInput from "./TimeInput";
import muiStyles from "../styles/muiStyles";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";

const DAY_CHIPS = [
  { label: "sun", dayOfWeek: 1 },
  { label: "mon", dayOfWeek: 2 },
  { label: "tue", dayOfWeek: 3 },
  { label: "wed", dayOfWeek: 4 },
  { label: "thu", dayOfWeek: 5 },
  { label: "fri", dayOfWeek: 6 },
  { label: "sat", dayOfWeek: 7 },
];

// IDs match the `time_units` lookup table seed: 4=minutes, 1=hours, 2=days.
// Display order per COMY1-279: Minutes / Hours / Days.
const TIME_UNITS = [
  { value: 4, key: "minutes" },
  { value: 1, key: "hours" },
  { value: 2, key: "days" },
];

// Mirror the mapping used in ServiceSettings so the same district labels appear
// everywhere: business-settings service areas dropdown + the per-section delivery
// areas list. Falls back to the raw name if a new district isn't in the map yet.
const ZONE_TRANSLATION_KEYS = {
  "North District": "north_district",
  "Haifa District": "haifa_district",
  "Jerusalem District": "jerusalem_district",
  "Tel Aviv District": "tel_aviv_district",
  "South District": "south_district",
  "Center District": "center_district",
};

// Match the rest of the app: green focus ring, neutral idle border.
const selectStyles = {
  width: "110px",
  height: "39px",
  borderRadius: "8px",
  fontSize: "14px",
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(37, 124, 109, 1)",
    borderWidth: "1px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(37, 124, 109, 0.7)",
  },
};

const wideSelectStyles = {
  ...selectStyles,
  width: "210px",
};

const dayChipStyles = (selected) => ({
  width: 38,
  height: 38,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: selected ? "1px solid rgba(37, 124, 109, 1)" : "1px solid rgba(165, 165, 165, 0.4)",
  backgroundColor: selected ? "rgba(37, 124, 109, 1)" : "transparent",
  color: selected ? "#fff" : "rgba(47, 43, 61, 0.9)",
  cursor: "pointer",
  fontSize: "13px",
  fontWeight: 500,
  textTransform: "capitalize",
});

const FulfillmentSettings = ({ kind, zones = [], isloading }) => {
  // kind: "store" | "restaurant"
  const fieldKey = kind === "store" ? "storeSettings" : "restaurantSettings";
  const { t } = useTranslation(["page-business-settings", "dropdowns"]);
  const { cardStyles, fieldsLabelStyles, fieldsLabelLightStyles } = muiStyles;
  const { values, setFieldValue } = useFormikContext();

  // Per COMY1-279 AC #6: the "Opening Hours & Working days" option is disabled
  // when no opening day is checked, since there is nothing for pickup to inherit.
  const hasAnyOpeningDay = (values.openingHours || []).some((d) => d?.enabled);

  // If the supplier had selected "Opening Hours" as the source but then
  // unchecked the last opening day, silently fall back to Custom so the
  // "At" dropdown doesn't stay on a now-disabled option.
  useEffect(() => {
    if (!hasAnyOpeningDay && values[fieldKey]?.pickup?.useStoreSchedule) {
      setFieldValue(`${fieldKey}.pickup.useStoreSchedule`, false);
    }
  }, [hasAnyOpeningDay, fieldKey, values, setFieldValue]);

  // A store ships in days; a restaurant delivers in minutes. Applies to the
  // blank-form default only — a saved value always wins.
  const defaultDeliveryTimeUnit = kind === "store" ? 2 : 4;
  const settings = values[fieldKey] || {
    pickup: {
      enabled: false,
      address: null,
      minPickupTime: null,
      minPickupTimeUnit: 2,
      useStoreSchedule: true,
      customSchedule: [],
    },
    delivery: {
      estimatedDeliveryTime: null,
      estimatedDeliveryTimeUnit: defaultDeliveryTimeUnit,
      areas: [],
    },
  };

  const updatePickup = (patch) =>
    setFieldValue(`${fieldKey}.pickup`, { ...settings.pickup, ...patch });
  const updatePickupAddress = (addrPatch) =>
    setFieldValue(`${fieldKey}.pickup.address`, {
      ...(settings.pickup.address || {}),
      ...addrPatch,
    });
  const updateDelivery = (patch) =>
    setFieldValue(`${fieldKey}.delivery`, { ...settings.delivery, ...patch });

  const isCustom = settings.pickup.useStoreSchedule === false;
  const customSlots = settings.pickup.customSchedule || [];
  // Per COMY1-279 AC #7: one From-To range applies to all selected days.
  // We still persist per-day rows (to match the schema) but the UI exposes
  // a single shared time, sourced from the first slot.
  const sharedFrom = customSlots[0]?.openTime || "";
  const sharedTo = customSlots[0]?.closeTime || "";

  const toggleDayChip = (dayOfWeek) => {
    const exists = customSlots.find((s) => s.dayOfWeek === dayOfWeek);
    if (exists) {
      setFieldValue(
        `${fieldKey}.pickup.customSchedule`,
        customSlots.filter((s) => s.dayOfWeek !== dayOfWeek)
      );
    } else {
      setFieldValue(`${fieldKey}.pickup.customSchedule`, [
        ...customSlots,
        { dayOfWeek, openTime: sharedFrom, closeTime: sharedTo },
      ]);
    }
  };

  // Update the shared time on every existing slot at once.
  const setSharedTime = (field, value) => {
    setFieldValue(
      `${fieldKey}.pickup.customSchedule`,
      customSlots.map((s) => ({ ...s, [field]: value }))
    );
  };

  const isZoneSelected = (zoneId) =>
    settings.delivery.areas.some((a) => a.districtId === zoneId);
  const getZoneCost = (zoneId) =>
    settings.delivery.areas.find((a) => a.districtId === zoneId)?.deliveryCost ?? null;

  const toggleZone = (zoneId) => {
    if (isZoneSelected(zoneId)) {
      updateDelivery({
        areas: settings.delivery.areas.filter((a) => a.districtId !== zoneId),
      });
    } else {
      // Use null (not 0) so the input renders empty and shows the "0"
      // placeholder. Typing into a literal "0" produces "05"-style concatenation
      // bugs with type="number".
      updateDelivery({
        areas: [
          ...settings.delivery.areas,
          { districtId: zoneId, deliveryCost: null, currency: 1 },
        ],
      });
    }
  };

  const setZoneCost = (zoneId, cost) => {
    updateDelivery({
      areas: settings.delivery.areas.map((a) =>
        a.districtId === zoneId ? { ...a, deliveryCost: cost } : a
      ),
    });
  };

  return (
    <Card sx={cardStyles}>
      <CustomTypography type="subheading">
        {t(kind === "store" ? "store_settings_heading" : "restaurant_settings_heading")}
      </CustomTypography>
      {/* Copy is per-vertical: the restaurant block used to talk about
          "products" and "a store", which read as the wrong section entirely. */}
      <Typography sx={{ ...fieldsLabelStyles, mt: "8px" }}>
        {t(
          kind === "store"
            ? "delivery_pickup_subheading"
            : "delivery_pickup_subheading_restaurant",
        )}
      </Typography>
      <Typography color="rgba(47, 43, 61, 0.9)" sx={{ ...fieldsLabelLightStyles, mt: "2px" }}>
        {t(
          kind === "store"
            ? "delivery_pickup_helper"
            : "delivery_pickup_helper_restaurant",
        )}
      </Typography>

      {/* PICKUP */}
      <Box sx={{ mt: "24px" }}>
        <Typography sx={{ ...fieldsLabelStyles, mb: "8px" }}>
          {t("pickup_settings")}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "12px" }}>
          <CustomCheckboxLabel
            checked={!!settings.pickup.enabled}
            onChange={(e) => {
              const enabled = e.target.checked;
              // Default the pickup address to the supplier's business
              // address when enabling pickup for the first time. The same
              // default fires on initial page load in BusinessSettings
              // (when an already-enabled pickup has no address). This
              // handler covers the runtime toggle so the supplier doesn't
              // have to re-type their own address right after checking
              // the box. We only do it when the address is currently
              // empty so we don't clobber a custom pickup location.
              // A city-only pick leaves `street` empty but still has a
              // city/placeId, so check all parts before defaulting to the
              // business address.
              const pickupAddr = settings.pickup.address;
              const hasAddress = !!(
                pickupAddr?.street ||
                pickupAddr?.city ||
                pickupAddr?.placeId
              );
              if (enabled && !hasAddress && values.businessLocation) {
                updatePickup({
                  enabled: true,
                  address: {
                    ...(settings.pickup.address || {}),
                    street: values.businessLocation,
                  },
                });
              } else {
                updatePickup({ enabled });
              }
            }}
            disabled={isloading}
          />
          <Typography sx={{ fontSize: "15px" }}>{t("i_have_pickup")}</Typography>
        </Box>

        {settings.pickup.enabled && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: "15px" }}>
                {t("pickup_from")}
              </Typography>
              <Box sx={{ flex: "0 0 auto", width: "320px" }}>
                <AddressMapInput
                  value={
                    // Re-display the saved location from its parts. `street`
                    // now holds only the route (not the full address), so for
                    // a city-only pick it's empty — compose street + building
                    // + city so the field still shows the chosen location.
                    [
                      settings.pickup.address?.street,
                      settings.pickup.address?.buildingNumber,
                      settings.pickup.address?.city,
                      settings.pickup.address?.country,
                    ]
                      .filter((p) => p && String(p).trim().length > 0)
                      .join(", ") || ""
                  }
                  placeholder={t("pickup_address_placeholder")}
                  disabled={isloading}
                  mapIcon
                  onPlaceSelected={(updates) => {
                    if (!updates || typeof updates === "string") {
                      updatePickupAddress({
                        street: null,
                        placeId: null,
                        city: null,
                        buildingNumber: null,
                        country: null,
                        longitude: null,
                        latitude: null,
                      });
                      return;
                    }
                    const lat = updates.latitude === "" ? null : Number(updates.latitude);
                    const lng = updates.longitude === "" ? null : Number(updates.longitude);
                    updatePickupAddress({
                      // Store only the parsed street (route) here — NOT the
                      // full formatted address. The city is saved separately
                      // below, so dumping the whole "street, city, country"
                      // string into `street` made the city show up twice
                      // wherever the parts get joined (e.g. the Members store
                      // view: "ירושלים, ישראל, ירושלים").
                      street: updates.street || null,
                      placeId: updates.place_id || null,
                      city: updates.city || null,
                      buildingNumber: updates.building_number || null,
                      country: updates.country || null,
                      latitude: Number.isFinite(lat) ? lat : null,
                      longitude: Number.isFinite(lng) ? lng : null,
                    });
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: "15px", minWidth: "200px" }}>
                {t("min_time_from_order_to_pickup")}
              </Typography>
              {/* Keep the number input + unit dropdown together so they wrap as one unit on mobile. */}
              <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <Box sx={{ flex: "0 0 auto", width: "85px" }}>
                  <InputField
                    name={`${fieldKey}.pickup.minPickupTime`}
                    type="number"
                    value={settings.pickup.minPickupTime ?? ""}
                    onChange={(e) =>
                      updatePickup({ minPickupTime: e.target.value === "" ? null : Number(e.target.value) })
                    }
                    placeholder="0"
                    disabled={isloading}
                    customSx={{ inputBaseRoot: { width: "85px" } }}
                  />
                </Box>
                <Select
                  value={settings.pickup.minPickupTimeUnit ?? 2}
                  onChange={(e) => updatePickup({ minPickupTimeUnit: Number(e.target.value) })}
                  disabled={isloading}
                  sx={selectStyles}
                >
                  {TIME_UNITS.map((u) => (
                    <MenuItem key={u.value} value={u.value}>
                      {t(`time_units.${u.key}`)}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: "15px" }}>{t("at_label")}</Typography>
              <Select
                value={isCustom ? "custom" : "store_schedule"}
                onChange={(e) =>
                  updatePickup({ useStoreSchedule: e.target.value === "store_schedule" })
                }
                disabled={isloading}
                sx={wideSelectStyles}
              >
                <MenuItem
                  value="store_schedule"
                  disabled={!hasAnyOpeningDay}
                  // Tooltip-ish hint via title attribute when disabled.
                  title={
                    !hasAnyOpeningDay ? t("use_store_schedule_disabled_hint") : undefined
                  }
                >
                  {t(
                    kind === "store"
                      ? "use_store_schedule"
                      : "use_restaurant_schedule",
                  )}
                </MenuItem>
                <MenuItem value="custom">{t("custom_days_and_hours")}</MenuItem>
              </Select>
            </Box>

            {isCustom && (
              <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                <Typography sx={{ fontSize: "15px", minWidth: "100px" }}>
                  {t("days_and_hours")}
                </Typography>
                {/* Group the 7 day chips so they wrap together on narrow screens
                    instead of breaking individually across multiple rows. */}
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                  {DAY_CHIPS.map((d) => (
                    <Box
                      key={d.dayOfWeek}
                      onClick={() => !isloading && toggleDayChip(d.dayOfWeek)}
                      sx={dayChipStyles(customSlots.some((s) => s.dayOfWeek === d.dayOfWeek))}
                    >
                      {t(`days_short.${d.label}`)}
                    </Box>
                  ))}
                </Box>
                {customSlots.length > 0 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Typography sx={{ fontSize: "15px" }}>{t("hours_label")}</Typography>
                    <TimeInput
                      value={sharedFrom}
                      onChange={(v) => setSharedTime("openTime", v || "")}
                      disabled={isloading}
                      placeholder={t("time_from")}
                    />
                    <Typography>-</Typography>
                    <TimeInput
                      value={sharedTo}
                      onChange={(v) => setSharedTime("closeTime", v || "")}
                      disabled={isloading}
                      placeholder={t("time_to")}
                    />
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>

      <Divider sx={{ my: "24px", borderColor: "rgba(165, 165, 165, 0.3)" }} />

      {/* DELIVERY */}
      <Box>
        <Typography sx={{ ...fieldsLabelStyles, mb: "8px" }}>
          {t(
            kind === "store"
              ? "delivery_settings_for_products"
              : "delivery_settings_for_menu_items",
          )}
        </Typography>
        <Typography color="rgba(47, 43, 61, 0.9)" sx={{ ...fieldsLabelLightStyles, mb: "12px" }}>
          {t("delivery_areas_helper")}
        </Typography>

        <Box
          sx={{
            display: "grid",
            // Auto columns so each area row is content-width and the two
            // columns sit side by side instead of being split across the card.
            gridTemplateColumns: { xs: "1fr", md: "auto auto" },
            justifyContent: "start",
            rowGap: "12px",
            columnGap: "32px",
          }}
        >
          {zones.map((zone) => {
            const selected = isZoneSelected(zone.id);
            const translationKey = ZONE_TRANSLATION_KEYS[zone.name];
            const zoneLabel = translationKey
              ? t(`dropdowns:zones.${translationKey}`)
              : zone.name;
            return (
              <Box
                key={zone.id}
                sx={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <CustomCheckboxLabel
                  checked={selected}
                  onChange={() => toggleZone(zone.id)}
                  disabled={isloading}
                />
                <Typography sx={{ fontSize: "14px", minWidth: "80px" }}>{zoneLabel}</Typography>
                <Typography sx={{ fontSize: "13px", color: "rgba(47, 43, 61, 0.6)" }}>
                  {t("delivery_extra_cost")}
                </Typography>
                <Box sx={{ flex: "0 0 auto", width: "65px" }}>
                  <InputField
                    name={`${fieldKey}.delivery.areas.${zone.id}.cost`}
                    type="number"
                    value={selected ? (getZoneCost(zone.id) ?? "") : ""}
                    onChange={(e) =>
                      setZoneCost(zone.id, e.target.value === "" ? null : Number(e.target.value))
                    }
                    disabled={isloading || !selected}
                    placeholder="0"
                    customSx={{ inputBaseRoot: { width: "65px" } }}
                  />
                </Box>
                {/* Per COMY1-279 review feedback: ILS mark sits outside the
                    input, after it in the DOM so it renders on the input's
                    left in the RTL (Hebrew) layout. */}
                <Typography sx={{ fontSize: "14px", color: "rgba(47, 43, 61, 0.6)" }}>
                  ₪
                </Typography>
              </Box>
            );
          })}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            mt: "24px",
            flexWrap: "wrap",
          }}
        >
          <Typography sx={{ fontSize: "15px" }}>
            {t(kind === "store" ? "delivery_time_est" : "delivery_time")}
          </Typography>
          {/* Keep the number input + unit dropdown together so they wrap as one unit on mobile. */}
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Box sx={{ flex: "0 0 auto", width: "85px" }}>
              <InputField
                name={`${fieldKey}.delivery.estimatedDeliveryTime`}
                type="number"
                value={settings.delivery.estimatedDeliveryTime ?? ""}
                onChange={(e) =>
                  updateDelivery({
                    estimatedDeliveryTime: e.target.value === "" ? null : Number(e.target.value),
                  })
                }
                placeholder="0"
                disabled={isloading}
                customSx={{ inputBaseRoot: { width: "85px" } }}
              />
            </Box>
            <Select
              value={
                settings.delivery.estimatedDeliveryTimeUnit ??
                defaultDeliveryTimeUnit
              }
              onChange={(e) =>
                updateDelivery({ estimatedDeliveryTimeUnit: Number(e.target.value) })
              }
              disabled={isloading}
              sx={selectStyles}
            >
              {TIME_UNITS.map((u) => (
                <MenuItem key={u.value} value={u.value}>
                  {t(`time_units.${u.key}`)}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

export default FulfillmentSettings;
